import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { ProductsService, BreadcrumbsService } from '@vestiaire/api-sdk-angular';
import { LocaleService } from '@app/core/i18n';
import { map, catchError } from 'rxjs/operators';
import { isPlatformServer } from '@angular/common';
import { ServerResponseService } from '@app/shared/services/server-response/server-response.service';
import { RoutingService } from '@app/core/services/routing.service';
import { Logger } from '@app/core/logging/logger.service';

@Injectable()
export class ProductPageResolver implements Resolve<any> {
  constructor(
    private productService: ProductsService,
    private localeService: LocaleService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private serverResponseService: ServerResponseService,
    private breadcrumbService: BreadcrumbsService,
    private routingService: RoutingService,
    private logger: Logger
  ) {}

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): any {
    const numberOfProductSimilar = 12;
    const productId = route.params.id;
    const url = this.routingService.buildPath(route.root.firstChild.url);
    const countryCode = this.localeService.getCountry();
    return forkJoin(
      this.productService.getProduct(productId, countryCode).pipe(
        map(res => {
          if (isPlatformServer(this.platformId)) {
            if (route.params.fullPath !== res.data.path) {
              this.serverResponseService.redirect(301, res.data.path);
              return null;
            }

            if (res.data.sold && res.data.shouldBeGone) {
              this.serverResponseService.setStatusOnly(410);
            }
          }
          return res.data;
        }),
        catchError(error => {
          this.logger.debug('[Product] resolver' + error.message + ': catchError', error);
          if (error.status === 404) {
            this.routingService.redirectError(error.status, state.url);
          } else {
            this.routingService.failover(state.url);
          }
          return of(null);
        })
      ),
      this.productService.listSimilarProducts(productId, numberOfProductSimilar).pipe(
        map(res => res.data),
        catchError(error => {
          this.logger.debug('[Product] resolver listSimilarProducts: ' + error.message, error);
          return of(null);
        })
      ),
      this.breadcrumbService.getBreadcrumb(encodeURIComponent(url)).pipe(
        map(res => res.data),
        catchError(err => {
          this.logger.debug('[Product] resolver getBreadcrumb: ' + err.message, err);
          return of([]);
        })
      ),
      this.productService.getProductShippingInformation(productId, countryCode).pipe(
        map(res => res.data),
        catchError(err => {
          this.logger.debug('[Product] resolver getProductShippingInformation: ' + err.message, err);
          return of([]);
        })
      )
    );
  }
}
